import { SiteHeader } from "@/components/site-header"

export default function BateaMobileFlowPage() {
  return (
    <>
      <SiteHeader title="App Mobile — Flujo de viajes Batea" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="max-w-5xl space-y-10">

          {/* ── Descripción ── */}
          <section>
            <h2 className="text-base font-semibold mb-2">Descripción general</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Los viajes de línea <strong>Batea</strong> tienen un flujo propio en la app mobile del chofer,
              distinto al flujo nacional/internacional. En lugar de un botón único "Confirmar viaje",
              el chofer recorre 5 hitos secuenciales que registran timestamps en la tabla{" "}
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">shipments</code>.
              Cada hito desbloquea el siguiente — la card avanza automáticamente cuando el servidor
              devuelve el campo actualizado en el poll silencioso (cada 10 s).
            </p>
          </section>

          {/* ── Flujo de hitos ── */}
          <section>
            <h2 className="text-base font-semibold mb-4">Flujo de hitos</h2>
            <div className="rounded-lg border overflow-hidden text-sm">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground w-6">#</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Botón en la app</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Campo que escribe</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Efecto adicional</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-4 py-3 text-muted-foreground">0</td>
                    <td className="px-4 py-3 font-medium">Confirmar viaje</td>
                    <td className="px-4 py-3 font-mono text-xs">shipments.status = activo</td>
                    <td className="px-4 py-3 text-muted-foreground">Solo visible cuando el envío está en estado <em>planificado</em></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-muted-foreground">1</td>
                    <td className="px-4 py-3 font-medium">Llegada al origen</td>
                    <td className="px-4 py-3 font-mono text-xs">shipments.origin_arrival_at</td>
                    <td className="px-4 py-3 text-muted-foreground">—</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-muted-foreground">2</td>
                    <td className="px-4 py-3 font-medium">Carga en origen</td>
                    <td className="px-4 py-3 font-mono text-xs">shipments.origin_loading_at</td>
                    <td className="px-4 py-3 text-muted-foreground">Bloqueado hasta que el chofer cargue la planificación del viaje</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-muted-foreground">3</td>
                    <td className="px-4 py-3 font-medium">Comenzar viaje</td>
                    <td className="px-4 py-3 font-mono text-xs">shipments.origin_departure_at</td>
                    <td className="px-4 py-3 text-muted-foreground">Pone shipment y trip en <em>en_curso</em>. Activa la ventana de llegada (44–48 hs)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-muted-foreground">4</td>
                    <td className="px-4 py-3 font-medium">Llegada al destino</td>
                    <td className="px-4 py-3 font-mono text-xs">shipments.destination_arrival_at</td>
                    <td className="px-4 py-3 text-muted-foreground">Muestra countdown ⏱ desde la salida. Verde si dentro de las 44 hs, rojo si superadas</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-muted-foreground">5</td>
                    <td className="px-4 py-3 font-medium">Descarga en destino</td>
                    <td className="px-4 py-3 font-mono text-xs">shipments.destination_unloading_at</td>
                    <td className="px-4 py-3 text-muted-foreground">Pone shipment en <em>finalizado</em> y trip en <em>completado</em>. Viaje pasa a "Anteriores"</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* ── Ventana de carga / llegada ── */}
          <section>
            <h2 className="text-base font-semibold mb-2">Ventana de carga y ventana de llegada</h2>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>
                <strong className="text-foreground">Ventana de carga</strong> — se configura en el envío
                ({" "}<code className="text-xs bg-muted px-1.5 py-0.5 rounded">shipments.loading_window</code>).
                Se muestra en la card en el paso inicial como rango horario (ej. 18:00 – 21:00) con la fecha
                de salida proyectada (<code className="text-xs bg-muted px-1.5 py-0.5 rounded">scheduled_origin_arrival_at</code>).
              </p>
              <p>
                <strong className="text-foreground">Ventana de llegada</strong> — calculada en el frontend a partir de{" "}
                <code className="text-xs bg-muted px-1.5 py-0.5 rounded">origin_departure_at</code>.
                La ventana es de 44 a 48 horas desde la salida. El countdown hace tick cada segundo
                y cambia de color verde → rojo al superar las 44 hs.
              </p>
            </div>
          </section>

          {/* ── Destino final ── */}
          <section>
            <h2 className="text-base font-semibold mb-2">Destino final (editable por el chofer)</h2>
            <p className="text-sm text-muted-foreground mb-3">
              El chofer puede cambiar el destino final en cualquier momento desde la card,
              porque en batea los choferes a veces son redirigidos a diferentes sectores de la planta.
              El valor se guarda en{" "}
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">shipments.final_destination</code>{" "}
              como un slug.
            </p>
            <div className="rounded-lg border overflow-hidden text-sm">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Slug</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Label en la app</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {[
                    { slug: "(null)", label: "Sin asignar" },
                    { slug: "añelo", label: "Añelo" },
                    { slug: "pozo-cercano", label: "Pozo Cercano" },
                    { slug: "pozo-lejano", label: "Pozo Lejano" },
                  ].map(row => (
                    <tr key={row.slug}>
                      <td className="px-4 py-2 font-mono text-xs">{row.slug}</td>
                      <td className="px-4 py-2">{row.label}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ── Permisos de driver en el API ── */}
          <section>
            <h2 className="text-base font-semibold mb-2">Permisos de driver en el API</h2>
            <p className="text-sm text-muted-foreground mb-3">
              El guard de <code className="text-xs bg-muted px-1.5 py-0.5 rounded">PATCH /api/shipments/:id</code>{" "}
              permite a los choferes actualizar únicamente los campos listados abajo.
              Cualquier otro campo retorna <strong>403 Forbidden</strong>.
            </p>
            <div className="rounded-md border bg-muted/30 p-4 font-mono text-xs leading-relaxed">
              status · tripPlan · originArrivalAt · originLoadingAt · originDepartureAt
              · destinationArrivalAt · destinationUnloadingAt · finalDestinationId
            </div>
            <p className="text-sm text-muted-foreground mt-3">
              El endpoint también valida que el chofer tenga un trip asignado al shipment
              antes de aplicar el update (join a través de{" "}
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">trips → mobiles → drivers → users</code>).
            </p>
          </section>

          {/* ── Estructura de componentes ── */}
          <section>
            <h2 className="text-base font-semibold mb-2">Estructura de componentes (driver app)</h2>
            <div className="rounded-md border bg-muted/30 p-4 font-mono text-xs leading-relaxed whitespace-pre">
{`apps/driver/
  hooks/
    useBateaHandlers.ts          — state + handlers para los 5 hitos
  components/
    batea/
      BateaTripActions.tsx       — botones secuenciales según el hito actual
      FinalDestinationPicker.tsx — bottom-sheet para elegir destino final
    TripCard.tsx                 — card genérica; usa BateaTripActions para batea
  app/(tabs)/
    index.tsx                    — HomeScreen; usa useBateaHandlers`}
            </div>
          </section>

          {/* ── Datos expuestos en GET /api/trips/me ── */}
          <section>
            <h2 className="text-base font-semibold mb-2">Datos expuestos en GET /api/trips/me</h2>
            <p className="text-sm text-muted-foreground mb-3">
              Además de los campos base del trip, el endpoint expone los siguientes campos
              del shipment para que la app pueda mostrar el estado correcto de cada hito:
            </p>
            <div className="rounded-md border bg-muted/30 p-4 font-mono text-xs leading-relaxed">
              workLine · shipmentStatus · originArrivalAt · originLoadingAt · originDepartureAt
              · destinationArrivalAt · destinationUnloadingAt · finalDestinationId
              · loadingWindow · scheduledOriginArrivalAt · finalDestinationId
            </div>
          </section>

        </div>
      </div>
    </>
  )
}
